import clsx from 'clsx';
import { type DietaryRestrictionsText } from '@hungryroot/dietary-restrictions';
import {
  type CartPairing,
  type RatablePairing,
  type RatableProduct,
  type LikeDislikeRatings,
  type FirstBoxPairingRecommendationIngredient,
  type NutritionAlt,
  type DishTypeV1,
} from '@hungryroot/rest';
import styles from './IngredientsImageGrid.module.css';

export enum Variant {
  Primary,
  Secondary,
}

interface OrderIngredient {
  id: number;
  name: string;
  brand_name: string;
  packaged_image_url: string | null;
  thumbnail_image_url: string | null;
  small_image2_url: string | null;
  small_image_url: string | null;
  card_image_url: string;
  amount: number;
  points: number;
  volume: number;
  is_ambient: boolean;
  slug: string;
}

interface Pairing {
  id: number;
  nutrition_html?: string;
  name: string;
  is_active: boolean;
  is_featured: boolean;
  average_rating: string;
  featured_img: string | null;
  featured_quote: string | null;
  sort_order: number;
  points: number;
  short_instruction_html: string;
  cooking_time: number;
  calories: number;
  servings: number;
  num_ratings: number;
  dish_type: DishTypeV1;
  slug: string;
  spicy_level: number;
  ingredients: Array<OrderIngredient>;
  quantity?: number;
  is_favorited: boolean;
  rating?: null;
  recipe_score?: number | null;
  liked?: LikeDislikeRatings | null;
  badges: Array<string>;
  tags: Array<number>;
  nutrition?: NutritionAlt;
}

export interface OrderIngredientViewModel extends OrderIngredient {
  dietaryIngredients?: DietaryRestrictionsText[];
  containsNeverItem?: boolean;
}

interface PairingWithIngredientDetails extends Omit<Pairing, 'ingredients'> {
  ingredients: OrderIngredientViewModel[];
}

interface Props {
  testid?: string;
  ingredients:
    | PairingWithIngredientDetails['ingredients']
    | CartPairing['ingredients']
    | RatablePairing['ingredients']
    | RatableProduct[]
    | FirstBoxPairingRecommendationIngredient[];
  variant?: Variant;
  className?: string;
}

function getImageContainerClassName(
  numImages: number,
  imageIndex: number
): string {
  if (numImages !== 3 || imageIndex > 3 || imageIndex < 1) {
    return styles['image-container'];
  }

  return clsx({
    [styles['images-3-image-1']]: imageIndex === 1,
    [styles['images-3-image-2']]: imageIndex === 2,
    [styles['images-3-image-3']]: imageIndex === 3,
  });
}

export function IngredientsImageGrid(props: Props) {
  const { testid, ingredients, variant, className } = props;

  const ingredientsWithImages = ingredients.filter(
    (ingredient) =>
      ingredient.thumbnail_image_url !== null ||
      ingredient.card_image_url !== null
  );
  const imageCount = Math.min(ingredientsWithImages.length, 4);

  let containerClassName;

  if (imageCount === 1) {
    containerClassName =
      variant === Variant.Secondary
        ? styles['container-1-secondary']
        : styles['container-1'];
  } else if (imageCount === 2) {
    containerClassName =
      variant === Variant.Secondary
        ? styles['container-2-secondary']
        : styles['container-2'];
  } else if (imageCount === 3) {
    containerClassName =
      variant === Variant.Secondary
        ? styles['container-3-secondary']
        : styles['container-3'];
  } else {
    containerClassName =
      variant === Variant.Secondary
        ? styles['container-4-secondary']
        : styles['container-4'];
  }

  const ingredientImagesToShow = ingredientsWithImages.slice(0, 4);
  const images = ingredientImagesToShow.map((ingredient, index) => {
    const { name, id, thumbnail_image_url, card_image_url } = ingredient;
    const className = getImageContainerClassName(imageCount, index + 1);
    const url = card_image_url ?? thumbnail_image_url ?? '';
    return (
      <div className={className} key={id}>
        <img
          loading="lazy"
          data-testid={`${testid}-image-${id}`}
          className={styles.image}
          src={url}
          alt={name}
        />
      </div>
    );
  });

  return (
    <div className={`${containerClassName} ${className}`} data-testid={testid}>
      {images}
    </div>
  );
}
